import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import { TILE_GUIDE } from '../data/tileGuide';

const OBSIDIAN = '#05050A';
const GOLD = '#D4AF37';
const GOLD_40 = 'rgba(212,175,55,0.4)';

/* ─── The 24 Imperium Rituals (shared with the Inscribe Menu guide) ─── */
const RITUALS = TILE_GUIDE.map((t) => ({
  id: t.id, name: `The ${t.name}`, tagline: t.tag, description: `${t.what} ${t.how}`,
  accent: t.accent, gradient: `linear-gradient(135deg, ${t.accent}2E, rgba(0,0,0,0))`, art: t.name.charAt(0),
}));

const RitualCard = ({ ritual, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-50px' }}
    transition={{ delay: (index % 6) * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: -6 }}
    className="group relative rounded-2xl border border-white/[0.06] overflow-hidden"
    style={{ background: '#0a0a10' }}
    data-testid={`ritual-card-${ritual.id}`}
  >
    {/* Accent glow */}
    <div className="absolute inset-0 opacity-60" style={{ background: ritual.gradient }} />
    {/* Top thread */}
    <div className="absolute top-0 left-0 right-0 h-[1px]" style={{ background: `linear-gradient(90deg, transparent, ${ritual.accent}, transparent)` }} />

    {/* Index sigil */}
    <div className="relative z-10 flex items-start justify-between p-5 sm:p-6 pb-3">
      <span className="font-['Cormorant_Garamond'] italic text-xs text-white/30 tracking-widest">
        {String(index + 1).padStart(2, '0')} / {RITUALS.length}
      </span>
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center text-xl"
        style={{
          border: `1px solid ${ritual.accent}55`,
          color: ritual.accent,
          boxShadow: `0 0 18px ${ritual.accent}33`,
          background: 'rgba(0,0,0,0.4)',
        }}
      >
        {ritual.art}
      </div>
    </div>

    {/* Body */}
    <div className="relative z-10 px-5 sm:px-6 pb-6">
      <h3 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-light text-white tracking-tight">
        {ritual.name}
      </h3>
      <p className="font-['Cormorant_Garamond'] italic text-sm mt-1" style={{ color: ritual.accent }}>
        {ritual.tagline}
      </p>
      <div className="w-10 h-[1px] mt-4 mb-4" style={{ background: `${ritual.accent}66` }} />
      <p className="font-['Outfit'] text-[13px] sm:text-sm text-white/50 leading-relaxed">
        {ritual.description}
      </p>
    </div>

    {/* Hover frame */}
    <div className="absolute inset-0 pointer-events-none border rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
         style={{ borderColor: `${ritual.accent}55`, boxShadow: `inset 0 0 30px ${ritual.accent}22` }} />
  </motion.div>
);

export default function Features() {
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []);

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: OBSIDIAN }} data-testid="features-page">
      {/* Ambient gold mist */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 20% 10%, ${GOLD_40} 0%, transparent 45%)` }} />
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 80% 90%, rgba(15,82,186,0.25) 0%, transparent 50%)` }} />
      </div>

      {/* Top bar */}
      <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-6 sm:pt-8 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/40 hover:text-[#D4AF37] transition-colors font-['Outfit'] text-xs uppercase tracking-[0.2em]"
          data-testid="features-back-btn"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <span className="font-['Outfit'] text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/60">The Royal Codex</span>
      </div>

      {/* Hero */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 pt-14 sm:pt-20 pb-14 text-center">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="flex items-center justify-center gap-3 mb-5">
          <div className="w-10 h-[1px] bg-[#D4AF37]/60" />
          <span className="font-['Outfit'] text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#D4AF37]/70">Twenty-Four Rituals · One Empire</span>
          <div className="w-10 h-[1px] bg-[#D4AF37]/60" />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="font-['Cormorant_Garamond'] text-4xl sm:text-6xl lg:text-7xl font-light text-white tracking-tight leading-[1.05]"
        >
          The Twenty-Four <span className="text-[#D4AF37] italic">Rituals</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.8 }}
          className="font-['Outfit'] text-sm sm:text-base text-white/45 mt-5 max-w-xl mx-auto leading-relaxed"
        >
          Each ritual is a chamber inside the Imperium. Sealed vaults, illuminated ledgers, sovereign decrees —
          <span className="text-[#D4AF37]/70"> all twenty-four, woven into a single sovereign life.</span>
        </motion.p>
      </section>

      {/* Grid */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {RITUALS.map((r, i) => <RitualCard key={r.id} ritual={r} index={i} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 max-w-3xl mx-auto px-6 pb-24 text-center">
        <div className="rounded-2xl border border-[#D4AF37]/20 p-8 sm:p-12" style={{ background: 'rgba(10,10,16,0.7)', boxShadow: `0 0 60px ${GOLD_40}` }}>
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-light text-white tracking-tight">
            Unlock all <span className="text-[#D4AF37]">twelve</span>
          </h2>
          <p className="font-['Outfit'] text-sm text-white/40 mt-3 max-w-md mx-auto leading-relaxed">
            The Imperium is private and free to enter. Claim your rank and inhabit every ritual.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-4">
            <motion.button
              whileHover={{ scale: 1.04, boxShadow: `0 0 30px ${GOLD_40}` }} whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/register')}
              className="px-8 sm:px-10 py-3.5 bg-[#D4AF37] text-black font-['Outfit'] text-xs uppercase tracking-[0.25em] hover:bg-[#E5C44D] transition-all duration-500 inline-flex items-center gap-2"
              data-testid="features-cta-register"
            >
              Claim Your Rank <ArrowRight size={14} weight="bold" />
            </motion.button>
            <button
              onClick={() => navigate('/login')}
              className="px-7 py-3.5 border border-white/15 text-white/60 font-['Outfit'] text-xs uppercase tracking-[0.25em] hover:border-[#D4AF37]/60 hover:text-[#D4AF37] transition-all duration-500"
              data-testid="features-cta-login"
            >
              Sign In
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/[0.05] py-7 px-4">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="font-['Cormorant_Garamond'] text-lg text-white/50 font-light"><span className="text-[#D4AF37]">#ARSH</span> · Imperium Core</span>
          <div className="flex items-center gap-5">
            <button onClick={() => navigate('/')} className="font-['Outfit'] text-[11px] text-white/30 hover:text-[#D4AF37] uppercase tracking-[0.2em]" data-testid="features-home">Home</button>
            <button onClick={() => navigate('/privacy')} className="font-['Outfit'] text-[11px] text-white/30 hover:text-[#D4AF37] uppercase tracking-[0.2em]" data-testid="features-privacy">Privacy</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
